/**
 * Analytics Utility
 * Centralized analytics tracking for Google Analytics 4 and future integrations
 */

// GA4 Measurement ID - .env'den al veya disabled bırak
const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || null;
const isValidGAId = GA_MEASUREMENT_ID && !GA_MEASUREMENT_ID.includes('XXXXXXXXXX');

/**
 * GA4 script'ini dinamik olarak yükle
 * Bu fonksiyon App.js'de çağrılmalı
 */
export const initAnalytics = () => {
    if (!isValidGAId) {
        if (process.env.NODE_ENV === 'development') {
            console.log('[Analytics] GA4 disabled - No valid measurement ID');
        }
        return;
    }

    // GA4 script'ini yükle
    const script = document.createElement('script');
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    script.async = true;
    document.head.appendChild(script);

    // gtag fonksiyonunu tanımla
    window.dataLayer = window.dataLayer || [];
    window.gtag = function () { window.dataLayer.push(arguments); };
    window.gtag('js', new Date());
    window.gtag('config', GA_MEASUREMENT_ID, {
        page_title: document.title,
        page_location: window.location.href,
        anonymize_ip: true,
        cookie_flags: 'SameSite=None;Secure'
    });

    if (process.env.NODE_ENV === 'development') {
        console.log('[Analytics] GA4 initialized with ID:', GA_MEASUREMENT_ID);
    }
};

// Track page views
export const trackPageView = (path, title) => {
    if (window.gtag) {
        window.gtag('event', 'page_view', {
            page_path: path,
            page_title: title,
            page_location: window.location.href
        });
    }
};

// Track custom events
export const trackEvent = (eventName, params = {}) => {
    if (window.gtag) {
        window.gtag('event', eventName, params);
    }

    // Console log in development
    if (process.env.NODE_ENV === 'development') {
        console.log(`[Analytics] Event: ${eventName}`, params);
    }
};

// E-commerce tracking
export const trackPurchase = (transactionId, value, currency = 'TRY', items = []) => {
    if (window.gtag) {
        window.gtag('event', 'purchase', {
            transaction_id: transactionId,
            value: value,
            currency: currency,
            items: items
        });
    }
};

// Track form submissions
export const trackFormSubmit = (formName, success = true) => {
    trackEvent('form_submit', {
        form_name: formName,
        success: success
    });
};

// Track button clicks
export const trackButtonClick = (buttonName, location) => {
    trackEvent('button_click', {
        button_name: buttonName,
        click_location: location
    });
};

// Track errors
export const trackError = (errorMessage, errorType = 'javascript') => {
    trackEvent('exception', {
        description: errorMessage,
        fatal: false,
        error_type: errorType
    });
};

// Track user engagement
export const trackEngagement = (engagementType, details = {}) => {
    trackEvent('engagement', {
        engagement_type: engagementType,
        ...details
    });
};

// Track signup/registration
export const trackSignUp = (method, plan = null) => {
    trackEvent('sign_up', {
        method: method,
        plan: plan
    });
};

// Track login
export const trackLogin = (method) => {
    trackEvent('login', {
        method: method
    });
};

// Track search
export const trackSearch = (searchTerm) => {
    trackEvent('search', {
        search_term: searchTerm
    });
};

// Set user properties
export const setUserProperties = (userId, properties = {}) => {
    if (window.gtag && GA_MEASUREMENT_ID) {
        window.gtag('set', 'user_properties', properties);
        if (userId) {
            window.gtag('config', GA_MEASUREMENT_ID, {
                user_id: userId
            });
        }
    }
};

// Track timing
export const trackTiming = (category, variable, value, label = null) => {
    trackEvent('timing_complete', {
        name: variable,
        value: value,
        event_category: category,
        event_label: label
    });
};

// Track scroll depth
export const trackScrollDepth = (percentage) => {
    trackEvent('scroll', {
        percent_scrolled: percentage
    });
};

// Track CTA clicks
export const trackCTAClick = (ctaName, destination) => {
    trackEvent('cta_click', {
        cta_name: ctaName,
        destination: destination
    });
};

export default {
    trackPageView,
    trackEvent,
    trackPurchase,
    trackFormSubmit,
    trackButtonClick,
    trackError,
    trackEngagement,
    trackSignUp,
    trackLogin,
    trackSearch,
    setUserProperties,
    trackTiming,
    trackScrollDepth,
    trackCTAClick
};
